import { Link } from 'react-router-dom';
import { Gallery } from '../components/Lightbox';
import { Section } from '../components/Section';
import { ShareButtons } from '../components/ShareButtons';
import { galleryPhotos } from '../lib/photos';
import { usePageMeta } from '../lib/usePageMeta';
import { SEO } from '../seo/pages';

/**
 * Alt text by gallery file name. Say only what the picture shows; a name goes
 * in only where the family has told us who it is (Shawna is the woman in the
 * GoFundMe photo on the donate page). A file with no entry gets a plain
 * fallback, so a new photo never needs a code change to appear.
 */
const CAPTIONS: Record<string, string> = {
  '01-baby-portrait.jpg': 'Baby portrait',
  '02-go-kart.jpg': 'A baby at the wheel of a go-kart',
  '03-two-kids.jpg': 'Two small children, an old family print',
  '04-family-portrait.jpg': 'Family portrait',
  '05-balloons.jpg': 'Two kids and a pile of balloons',
  '06-christmas-fireplace.jpg': 'Christmas by the fireplace',
  '07-family-portrait-2.jpg': 'Family portrait',
  '08-football.jpg': 'In a Cougars football jersey, number 60',
  '09-school-portrait.jpg': 'School portrait',
  '10-graduation.jpg': 'Graduation day in cap and gown',
  '11-brandon-90.jpg': 'Brandon, the print signed Brandon 90',
  '12-white-guitar.jpg': 'Brandon with a white electric guitar',
  '13-blue-guitar.jpg': 'Brandon with a blue electric guitar',
  '14-acoustic-on-stage.jpg': 'Brandon on stage with an acoustic guitar',
  '15-portrait-bw.jpg': 'Brandon, black and white portrait',
  '16-on-stage.jpg': 'On stage under the lights',
  '17-velkra.jpg': 'On stage in front of a Velkra banner',
  '18-friends-90s.jpg': 'Brandon with a friend',
  '19-friends.jpg': 'Brandon with a friend',
  '20-newborn.jpg': 'Brandon holding a newborn',
  '21-cruise-bw.jpg': 'Brandon on a ship deck, black and white',
  '22-gym.jpg': 'Brandon at the gym with a dumbbell',
  '23-gym-2.jpg': 'Brandon at the gym',
  '24-dolphin.jpg': 'Brandon swimming with a dolphin',
  '25-sombrero.jpg': 'Brandon in a sombrero and serape',
  '26-carnival-paradise.jpg': 'In front of the Carnival Paradise',
  '27-ocean.jpg': 'Standing in clear ocean water',
  '28-captain-hats.jpg': 'Captain hats on a cruise balcony',
  '29-boardwalk.jpg': 'On the boardwalk at night',
  '30-couple-bw.jpg': 'Black and white portrait',
  '31-dinner.jpg': 'Dinner out',
  '32-cruise-cabin.jpg': 'In a cruise cabin',
  '33-tigers-shirt.jpg': 'Brandon in a black tank top',
  '34-cat-nap.jpg': 'Brandon asleep with a white cat',
  '35-kitten.jpg': 'Brandon with a tabby kitten',
  '36-cat-floor.jpg': 'Brandon on the floor with a cat',
  '37-holidays.jpg': 'Holiday pyjamas',
  '38-sequin-jacket.jpg': 'Brandon in a sequin jacket',
  '39-thumbs-up.jpg': 'Thumbs up and a flexed arm',
  '40-winter-selfie.jpg': 'Brandon and Shawna, winter selfie',
  '41-car-selfie.jpg': 'Four friends in a car',
  '42-leopard.jpg': 'Brandon and Shawna',
  '43-smiles.jpg': 'Brandon and Shawna',
  '44-icandy-energy.jpg': 'Brandon and Shawna in front of an iCandy Energy backdrop',
  '45-sequins.jpg': 'Brandon with a friend in sequins',
  '46-dj-booth.jpg': 'Brandon with a friend at a DJ booth',
  '47-concert.jpg': 'At a concert',
  '48-dinner-table.jpg': 'Dinner with friends',
  '49-night-out.jpg': 'A night out with friends',
  '50-friends-2.jpg': 'Brandon with friends',
  '51-sunglasses.jpg': 'Four friends in sunglasses',
  '52-moonshine-crew.jpg': 'Four friends in Moonshine Mountain shirts',
  '53-icandy-tent.jpg': 'Under the iCandy Clothing tent',
  '54-lanyards.jpg': 'Brandon with a friend, festival lanyards on',
  '55-fist-bump.jpg': 'Fist bump',
  '56-fairground.jpg': 'Friends at the fairground',
  '57-ferris-wheel.jpg': 'Under a lit Ferris wheel at night',
  '58-rainbow.jpg': 'Sitting on a rainbow arch',
  '59-crew.jpg': 'The crew',
  '60-night-out-2.jpg': 'Brandon with a friend at night',
  '61-three-friends.jpg': 'Three friends',
  '62-blue-lights.jpg': 'Two friends under blue lights',
  '63-mic.jpg': 'Brandon with a friend and a microphone',
  '64-railing.jpg': 'Brandon leaning on a balcony railing',
  '65-cap.jpg': 'Brandon in an iCandy cap',
  '66-icandy-banner.jpg': 'In front of an iCandy Clothing banner',
  '67-coffee.jpg': 'Coffee with a friend',
  '68-arms-up.jpg': 'Brandon, arms up on the dance floor',
  '69-moonshine-crew-2.jpg': 'Four friends in Moonshine Mountain shirts',
  '70-icandy-booth.jpg': 'The iCandy Clothing booth',
  '71-red-carpet.jpg': 'Red carpet, all in white',
  '72-schools-out.jpg': 'A Schools Out party',
  '73-group.jpg': 'Group photo',
  '74-step-and-repeat.jpg': 'On the step-and-repeat',
  '75-crowd.jpg': 'A big group in front of a lit backdrop',
};

/** The family's photos of Brandon, in the order of the gallery folder. */
export function PhotosPage() {
  usePageMeta(SEO.photos.title, SEO.photos.description, { jsonLd: SEO.photos.jsonLd });
  const photos = galleryPhotos().map((photo, i) => ({
    name: photo.name,
    url: photo.url,
    thumb: photo.thumb,
    alt: CAPTIONS[photo.name] ?? `Brandon, photo ${i + 1}`,
  }));

  return (
    <>
      <Section eyebrow="#BrandonStrong" title="Photos">
        <p className="max-w-2xl text-xl text-ink-200">
          From the family and friends, across the years. Tap a photo to open it, then use the
          arrows to move through them.
        </p>
        <Gallery className="mt-10" photos={photos} />
        <ShareButtons
          className="mt-8"
          title="Photos of Brandon"
          text="Photos of Brandon from his family and friends. #BrandonStrong"
        />
      </Section>

      <section className="border-t border-white/10 bg-night-900">
        <div className="mx-auto flex max-w-6xl flex-col items-start gap-6 px-4 py-16 md:flex-row md:items-center md:justify-between">
          <h2 className="text-4xl">Bring your own to the show.</h2>
          <div className="flex gap-4">
            <Link to="/rooms" className="btn-primary">
              Reserve a room
            </Link>
            <Link to="/event" className="btn-outline">
              The weekend
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
